import * as React from 'react'
import dynamic from 'next/dynamic'
import {motion} from 'framer-motion'
import get from 'lodash/get'

import Header from '@components/demo/Header'
import Footer from '@components/demo/Footer'
import Contact from '@components/demo/Contact'
import ImgSet from '@components/demo/ImgSet'
import UploadForm from '@components/demo/UploadForm'
import UploadResult from '@components/demo/UploadResult'
import CTAButton from '@components/demo/CTAButton'

import {t, axios, sleep} from '@lib/helpers'

const Meta = dynamic(() => import('@components/Meta'))

const Demographic = ({isRecaptchaValidated}) => {
  return (
    <React.Fragment>
      <Meta title={`Demographic – ${t('layout.title')}`} />

      <div className='DemoDetail Demographic'>
        <Header name='Demographic' />

        <div className='DemoDetail__wrapper'>
          <Hero />
          <Why />
        </div>

        <Upload isRecaptchaValidated={isRecaptchaValidated} />

        <Contact isRecaptchaValidated={isRecaptchaValidated} />

        <Footer />
      </div>
    </React.Fragment>
  )
}

const Hero = () => {
  const data = [
    {
      src: '/images/demo/demographic/01-girl.jpg',
      tag1: t('pages.demo.demographic.hero.label1.description'),
      tag2: t('pages.demo.demographic.hero.label1.age'),
    },
    {
      src: '/images/demo/demographic/02-man.jpg',
      tag1: t('pages.demo.demographic.hero.label2.description'),
      tag2: t('pages.demo.demographic.hero.label2.age'),
    },
    {
      src: '/images/demo/demographic/03-woman.jpg',
      tag1: t('pages.demo.demographic.hero.label3.description'),
      tag2: t('pages.demo.demographic.hero.label3.age'),
    },
    {
      src: '/images/demo/demographic/04-man.jpg',
      tag1: t('pages.demo.demographic.hero.label4.description'),
      tag2: t('pages.demo.demographic.hero.label4.age'),
    },
    {
      src: '/images/demo/demographic/05-woman.jpg',
      tag1: t('pages.demo.demographic.hero.label5.description'),
      tag2: t('pages.demo.demographic.hero.label5.age'),
    },
  ]
  return (
    <section className='DemoDetail__hero'>
      <ul className='DemoDetail__feature'>
        {data.map((item, i) => (
          <li key={item.src}>
            <motion.div
              initial='hidden'
              animate='visible'
              transition={{duration: 0.6, delay: i * 0.1}}
              variants={{
                visible: {y: 0},
                hidden: {y: '-100%'},
              }}
              className='outer'
            >
              <motion.div
                initial='hidden'
                animate='visible'
                transition={{duration: 0.6, delay: i * 0.1}}
                variants={{
                  visible: {y: 0},
                  hidden: {y: '100%'},
                }}
                className='inner'
              >
                <ImgSet src={item.src} />
              </motion.div>
            </motion.div>

            <motion.div
              initial='hidden'
              animate='visible'
              transition={{duration: 0.2, delay: 0.6 + i * 0.1}}
              variants={{
                visible: {opacity: 1},
                hidden: {opacity: 0},
              }}
            >
              <motion.div
                animate={{scale: 0.95}}
                transition={{
                  duration: 0.5,
                  yoyo: Infinity,
                }}
                className='frame'
              />
              <p>
                {item.tag1}
                <br />
                {item.tag2}
              </p>
            </motion.div>
          </li>
        ))}
      </ul>

      <div className='container'>
        <div className='DemoDetail__hero__content'>
          <h1 className='DemoDetail__title DemoDetail__title--hilight'>
            {t('pages.demo.demographic.hero.title')}
          </h1>
          <h4>{t('pages.demo.demographic.hero.subtitle')}</h4>
          <CTAButton />
        </div>
      </div>
    </section>
  )
}

const Why = () => {
  return (
    <section className='DemoDetail__why'>
      <div className='container' id='why'>
        <div className='DemoDetail__why__img'>
          <img
            src='/images/demo/demographic/why.jpg'
            srcSet='/images/demo/demographic/why@2x.jpg 2x, /images/demo/demographic/why@3x.jpg 3x'
            alt='Face Verification'
          />
        </div>

        <div className='DemoDetail__why__content'>
          <h2
            className='DemoDetail__title DemoDetail__title--hilight'
            dangerouslySetInnerHTML={{
              __html: t('pages.demo.demographic.why.title'),
            }}
          />
          <p>{t('pages.demo.demographic.why.description')}</p>
        </div>
      </div>
    </section>
  )
}

const Upload = ({isRecaptchaValidated}) => {
  const [result, setResult] = React.useState('')

  const fetchResult = async (slug, retries) => {
    const response = await axios.get(`/demo/demographics/${slug}`, {
      headers: {
        'X-Api-Key': 'Data wow',
      },
    })

    if (get(response, 'data.data.status') === 'created') {
      return get(response, 'data.data.results')
    }

    if (retries > 0) {
      await sleep(2000)
      return fetchResult(slug, retries - 1)
    }

    return 'Timeout! please try again'
  }

  const handleChange = async (file) => {
    const formData = new FormData()
    formData.append('image', file)
    setResult('')

    try {
      const postResponse = await axios.post('/demo/demographics', formData, {
        headers: {
          'X-Api-Key': 'Data wow',
        },
      })

      if (get(postResponse, 'data.status') !== 'OK') {
        setResult('Something wrong please try again.')
        return
      }

      const result = await fetchResult(get(postResponse, 'data.data.slug'), 15)
      setResult(JSON.stringify(result, null, 2))
    } catch (err) {
      setResult('Something wrong please try again.')
    }
  }

  return (
    <section className='DemoDetail__upload' id='upload'>
      <header>
        <div className='container'>
          <h2
            className='DemoDetail__title DemoDetail__title--hilight'
            dangerouslySetInnerHTML={{
              __html: t('pages.demo.demographic.upload.title'),
            }}
          />
          <p>{t('pages.demo.demographic.upload.description')}</p>
        </div>
      </header>

      <hr />

      <div className='DemoDetail__upload__content'>
        <UploadForm
          name='WowDemo'
          onChange={handleChange}
          isLoading={isRecaptchaValidated}
        />

        <UploadResult result={result} />
      </div>
    </section>
  )
}

export default Demographic
